'use client';

import { MoveRightIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useLatest, useMountedState } from 'react-use';

import { createNextRouteHref } from '@/src/lib/util/routing/createNextRouteHref';
import type { Category, ExampleCategory, ExamplesBlock } from '@/types';
import { Button } from '@/ui/Button/Button';
import { ButtonLink } from '@/ui/Button/ButtonLink';

import { BlockContent } from './BlockContent';
import { ALL_EXAMPLES_CATEGORY, ALL_EXAMPLES_CATEGORY_ID } from './constants';
import { fetchExamplesPageAction } from './lib/fetchExamplesPageAction';

import styles from './Examples.module.scss';

type ExamplesPage = Awaited<ReturnType<typeof fetchExamplesPageAction>>;

type Props = Pick<ExamplesBlock, 'category' | 'intro' | 'has_intro' | 'limit'> & {
    categories: ExampleCategory[];
    isHero?: boolean;
    initialExamplesPage: ExamplesPage;
    initialPageSize?: number;
    showFilters?: boolean;
};

const DEFAULT_PAGE_SIZE = 6;

export function DynamicExamples({
    category,
    categories,
    initialExamplesPage,
    has_intro: hasIntro,
    initialPageSize = DEFAULT_PAGE_SIZE,
    intro,
    limit: customLimit,
    isHero,
    showFilters = true,
}: Props) {
    const getIsMounted = useMountedState();
    const [pageSize, setPageSize] = useState(initialPageSize);
    const [examplesPage, setExamplesPage] = useState<ExamplesPage>(initialExamplesPage);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string>();
    const initialCategoryId = category?.id ?? ALL_EXAMPLES_CATEGORY_ID;
    const [selectedCategoryId, setSelectedCategoryId] = useState(initialCategoryId);
    const selectedCategoryIdRef = useLatest(selectedCategoryId);
    const selectedCategory = categories.find(({ id }) => id === selectedCategoryId);
    const isAllExamplesCategory = selectedCategoryId === ALL_EXAMPLES_CATEGORY_ID;
    const isCategoryFilterApplied = Boolean(
        !category && selectedCategoryId && !isAllExamplesCategory,
    );

    const loadPage = async (
        categoryId: Category['id'] | undefined,
        config: { pageNumber: number; pageSize: number },
    ) => {
        setError(undefined);
        setIsLoading(true);
        try {
            const page = await fetchExamplesPageAction(categoryId, {
                page: config.pageNumber,
                limit: config.pageSize,
            });

            if (getIsMounted() && selectedCategoryIdRef.current === categoryId) {
                setIsLoading(false);
                setExamplesPage((current) => ({
                    ...page,
                    docs: [...current.docs, ...page.docs],
                }));
            }
        } catch {
            if (getIsMounted() && selectedCategoryIdRef.current === categoryId) {
                setIsLoading(false);
                setError('Could not load examples. Please try again.');
            }
        }
    };

    const handleLoadMore = async () => {
        loadPage(selectedCategoryId, { pageNumber: examplesPage.nextPage!, pageSize });
    };

    const handleSelectCategory = (categoryId: Category['id'] | undefined) => {
        const nextPageSize = categoryId === ALL_EXAMPLES_CATEGORY_ID ? 6 : 12;
        setSelectedCategoryId(categoryId);
        setPageSize(nextPageSize);
        setExamplesPage((current) => ({ ...current, docs: [] }));
        loadPage(categoryId, { pageNumber: 0, pageSize: nextPageSize });
    };

    const pagination = isCategoryFilterApplied ? (
        <ButtonLink
            className={styles.actions}
            href={createNextRouteHref({
                ...selectedCategory!,
                contentType: 'example-category',
            })}
            iconClassName={styles.loadMoreIcon}
            iconRight={MoveRightIcon}
        >
            See more {selectedCategory!.title} press releases
        </ButtonLink>
    ) : (
        <Button disabled={isLoading} className={styles.actions} onClick={handleLoadMore}>
            Load more
        </Button>
    );

    const actions = [ALL_EXAMPLES_CATEGORY, ...categories].map(({ title, id }) => (
        <Button
            key={id || title}
            active={id === selectedCategoryId}
            variant={id === selectedCategoryId ? 'dark' : 'secondary'}
            onClick={() => handleSelectCategory(id)}
            size="large"
        >
            {title}
        </Button>
    ));

    useEffect(() => {
        setError(undefined);
    }, [selectedCategoryId]);

    return (
        <BlockContent
            filters={showFilters ? <div className={styles.filters}>{actions}</div> : undefined}
            actions={
                <>
                    {error && <p className={styles.error}>{error}</p>}
                    {examplesPage.hasNextPage && !customLimit ? pagination : null}
                </>
            }
            intro={intro}
            isHero={isHero}
            hasIntro={hasIntro}
            examples={examplesPage.docs}
        />
    );
}
